import {
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Dialog as MuiDialog,
  type DialogProps as MuiDialogProps,
  type SxProps,
  type Theme,
} from '@mui/material';
import React from 'react';

export type DialogProps = Omit<
  MuiDialogProps,
  'title' | 'children' | 'aria-labelledby' | 'aria-describedby'
> & {
  /** Required: it is the dialog's accessible name. */
  title: string;
  /** Wired as the dialog's accessible description. */
  description?: React.ReactNode;
  /** Buttons, right-aligned, 8px apart. */
  actions?: React.ReactNode;
  children?: React.ReactNode;
};

/**
 * A modal whose title is required and is its accessible name, and whose
 * description is wired as its accessible description. MUI 9 gives the focus
 * trap, Escape, focus restore and the title wiring (via its `titleId`
 * context) but never aria-describedby — so the confirmation text is not
 * read on open — and nothing stops a dialog shipping without a title. Max
 * width 420 unless the caller asks for an MUI `maxWidth` breakpoint.
 */
export const Dialog = ({
  title,
  description,
  actions,
  children,
  maxWidth,
  slotProps,
  ...rest
}: DialogProps) => {
  const titleId = React.useId();
  const descriptionId = React.useId();
  // `maxWidth` undefined = the design-system 420 cap; an explicit MUI
  // breakpoint (or `false` for no cap) is the caller's choice.
  const paperSx =
    maxWidth === undefined
      ? {width: 'calc(100% - 64px)', maxWidth: 420}
      : undefined;
  const callerPaper = slotProps?.paper;
  if (typeof callerPaper === 'function') {
    throw new Error('ui/Dialog: slotProps.paper must be an object');
  }
  const callerSx = (callerPaper as {sx?: SxProps<Theme>} | undefined)?.sx;
  return (
    <MuiDialog
      {...rest}
      maxWidth={maxWidth ?? false}
      aria-labelledby={titleId}
      aria-describedby={description ? descriptionId : undefined}
      slotProps={{
        ...slotProps,
        paper: {
          ...callerPaper,
          sx: [
            ...(paperSx ? [paperSx] : []),
            ...(Array.isArray(callerSx)
              ? callerSx
              : callerSx
                ? [callerSx]
                : []),
          ],
        },
      }}>
      <DialogTitle id={titleId}>{title}</DialogTitle>
      {(description || children) && (
        <DialogContent>
          {description && (
            <DialogContentText id={descriptionId}>
              {description}
            </DialogContentText>
          )}
          {children}
        </DialogContent>
      )}
      {actions && <DialogActions>{actions}</DialogActions>}
    </MuiDialog>
  );
};
